<template>
  <v-container class="bordered bg-white">
    <DataTable
      :headers="headers"
      :items="labours" />

    <v-row>
      <v-col>
        <v-table class="bottom_table">
          <tbody>
            <tr>
              <td class="wide_col">
                {{
                  $t(
                    "entities.manufacturingStep.fields.labour_costs_manufacturing_step_part_long"
                  )
                }}
              </td>
              <td class="input_col">
                <CalcOutput
                  :id="nodeId"
                  field="labour_costs_manufacturing_step_part"
                  element-type="manufacturingStep"
                  hide-label
                  width="80" />
              </td>
              <td class="e_per_piece_col">
                {{ $t("units.e_per_piece_long") }}
              </td>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { numericFieldInTable, stringFieldInTable } from "@/utils/helper";
import { useSpaceStore } from "@/stores/spaceStore";
import { useTreeStore } from "@/stores/treeStore";
import CalcOutput from "../../../../components/Elements/CalcOutput.vue";

import DataTable from "@/components/Elements/DataTable.vue";

const spaceStore = useSpaceStore();
const treeStore = useTreeStore();

const { t } = useTranslation();
const step = ref(null);

const props = defineProps({
  nodeId: {
    type: String,
    required: true
  }
});

const part = computed(() => {
  return spaceStore.getElement(props.nodeId);
});

const labours = computed(() => {
  const activities = Object.entries(spaceStore.calculationElements)
    .reduce((result, [id, element]) => {
      if (id.endsWith("activity")) {
        result.push(Object.assign({}, element, { id }));
      }
      return result;
    }, [])
    .filter(
      (activity) =>
        activity.location === treeStore.elementLocation[props.nodeId]
    )
    .map((activity) => {
      return {
        value: activity.id,
        title: activity.name,
        rate: Number(activity.personnel_hourly_rate_activity)
      };
    });

  return spaceStore.getElementList(props.nodeId, "labour").map((value) => {
    const activity = activities.find(
      (element) => element.value === value.activity_labour
    ) || {
      value: "",
      title: "",
      rate: 0
    };

    return {
      ...value,
      activity_labour: activity.title,
      cycle_time_part: part.value.cycle_time_part,
      share_of_the_lot_percentage_part:
        part.value.share_of_the_lot_percentage_part,
      number_of_parts_part: part.value.number_of_parts_part,
      labour_costs_labour_part:
        ((part.value.cycle_time_part / 3600) *
          (part.value.share_of_the_lot_percentage_part / 100) *
          (value.labour_retention_percentage_labour / 100) *
          value.personnel_hourly_rate_labour) /
        part.value.number_of_parts_part
    };
  });
});

onMounted(() => {
  step.value = spaceStore.calculationElements[props.nodeId];
});

const headers = computed(() => [
  stringFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "activity_labour"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "labour_retention_percentage_labour"
  }),
  numericFieldInTable({ t, entity: "part", field: "cycle_time_part" }),
  numericFieldInTable({ t, entity: "part", field: "number_of_parts_part" }),
  numericFieldInTable({
    t,
    entity: "part",
    field: "share_of_the_lot_percentage_part"
  }),
  numericFieldInTable({
    t,
    entity: "labour",
    field: "personnel_hourly_rate_labour"
  }),
  numericFieldInTable({ t, entity: "part", field: "labour_costs_labour_part" })
]);
</script>

<style scoped lang="scss">
:deep() .v-data-table-header__content {
  font-weight: 700;
  font-size: smaller;
}

:deep()
  .border-table
  .v-table__wrapper
  > table
  > thead
  > tr
  > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep()
  .border-table
  .v-table__wrapper
  > table
  > tbody
  > tr
  > td:not(:last-child),
.border-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.bottom_table {
  font-size: 85%;

  .wide_col {
    width: 280px;
  }

  .input_col {
    //width: 80px;
    padding: 0 6px !important;

    .v-field__input {
      min-height: 10px !important;
    }
  }

  .e_per_piece_col {
    width: 30px;
    padding: 0 6px !important;
    white-space: nowrap;
  }

  td,
  th {
    border-bottom: none !important;
  }

  .low-height {
    td,
    th {
      height: 40px !important;
    }
  }

  .v-field__input {
    padding: 4px 8px !important;
  }
}
</style>
